import React, { useRef } from 'react';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Toolbar,
  ExcelExport,
  PdfExport,
  ColumnChooser,
} from '@syncfusion/ej2-react-grids';
import has from 'lodash/has';
import toString from 'lodash/toString';
import { getFromLocalStorage } from 'localStorageService/LocalStorage';
import { PdfTrueTypeFont } from '@syncfusion/ej2-pdf-export';
import { calibriFont } from 'constant/PdfFont';
// constants
import { localStorageKeys } from 'constant/Enums';

const SalesReportDataGrid = ({ data }) => {
  const toolbarOptions = ['ExcelExport', 'PdfExport', 'CsvExport', 'ColumnChooser', 'Search'];
  const ref = useRef();
  const opsCenter = getFromLocalStorage(localStorageKeys.OPS_CENTER);

  const toolbarClick = (args) => {
    const pdfExportProperties = {
      theme: {
        caption: { font: new PdfTrueTypeFont(calibriFont, 10) },
        header: { font: new PdfTrueTypeFont(calibriFont, 12) },
        record: { font: new PdfTrueTypeFont(calibriFont, 9) },
      },
    };
    switch (args.item.text) {
      case 'PDF Export':
        ref.current.pdfExport(pdfExportProperties);
        break;
      case 'Excel Export':
        ref.current.excelExport();
        break;
      case 'CSV Export':
        ref.current.csvExport();
        break;
      default:
        break;
    }
  };

  const excelQueryCellInfo = (args) => {
    if (has(args.column, 'customExcelTemplate')) {
      args.value = toString(args.column.customExcelTemplate(args.data));
    }
  };

  return (
    <div className="control-pane">
      <div className="control-section">
        <GridComponent
          dataSource={data}
          allowPaging={true}
          toolbar={toolbarOptions}
          allowExcelExport={true}
          allowPdfExport={true}
          ref={ref}
          toolbarClick={toolbarClick}
          showColumnChooser={true}
          excelQueryCellInfo={excelQueryCellInfo}
          pdfQueryCellInfo={excelQueryCellInfo}
          rowHeight={60}
          allowTextWrap={true}
          pageSettings={{ pageSizes: true, pageSize: 20 }}
        >
          <ColumnsDirective>
            <ColumnDirective headerText="Date" field="date" width={120} textAlign="Center" />
            <ColumnDirective
              headerText="COD Orders"
              field="cashCount"
              width={80}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="K-NET Orders"
              field="kentCount"
              width={80}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="Credit Orders"
              field="creditCount"
              width={80}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="Paypal Orders"
              field="paypalCount"
              width={80}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="Total Orders"
              field="count"
              width={100}
              textAlign="Center"
            />
            <ColumnDirective headerText="COD" field="cashTotal" width={100} textAlign="Center" />
            <ColumnDirective
              headerText="K-NET"
              template={(rowData) => (
                <span>
                  {rowData.kentTotal != null &&
                    rowData.kentTotal.toFixed(opsCenter?.country?.decimalNumber ?? 3)}
                </span>
              )}
              customExcelTemplate={(rowData) =>
                rowData.kentTotal != null &&
                rowData.kentTotal.toFixed(opsCenter?.country?.decimalNumber ?? 3)
              }
              width={100}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="K-NET(TAP)"
              template={(rowData) => (
                <span>
                  {rowData.kentTapTotal != null &&
                    rowData.kentTapTotal.toFixed(opsCenter?.country?.decimalNumber ?? 3)}
                </span>
              )}
              customExcelTemplate={(rowData) =>
                rowData.kentTapTotal != null &&
                rowData.kentTapTotal.toFixed(opsCenter?.country?.decimalNumber ?? 3)
              }
              width={100}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="Credit"
              template={(rowData) => (
                <span>
                  {rowData.creditTotal != null &&
                    rowData.creditTotal.toFixed(opsCenter?.country?.decimalNumber ?? 3)}
                </span>
              )}
              customExcelTemplate={(rowData) =>
                rowData.creditTotal != null &&
                rowData.creditTotal.toFixed(opsCenter?.country?.decimalNumber ?? 3)
              }
              width={80}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="Credit(TAP)"
              template={(rowData) => (
                <span>
                  {rowData.creditTapCount != null &&
                    rowData.creditTapCount.toFixed(opsCenter?.country?.decimalNumber ?? 3)}
                </span>
              )}
              customExcelTemplate={(rowData) =>
                rowData.creditTapCount != null &&
                rowData.creditTapCount.toFixed(opsCenter?.country?.decimalNumber ?? 3)
              }
              width={100}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="Paypal"
              template={(rowData) => (
                <span>
                  {rowData.paypalTotal != null &&
                    rowData.paypalTotal.toFixed(opsCenter?.country?.decimalNumber ?? 3)}
                </span>
              )}
              customExcelTemplate={(rowData) =>
                rowData.paypalTotal != null &&
                rowData.paypalTotal.toFixed(opsCenter?.country?.decimalNumber ?? 3)
              }
              width={100}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="Grand Total"
              template={(rowData) => (
                <span>
                  {rowData.total != null &&
                    rowData.total.toFixed(opsCenter?.country?.decimalNumber ?? 3)}
                </span>
              )}
              customExcelTemplate={(rowData) =>
                rowData.total != null &&
                rowData.total.toFixed(opsCenter?.country?.decimalNumber ?? 3)
              }
              width={100}
              textAlign="Center"
            />
          </ColumnsDirective>
          <Inject services={[Toolbar, ExcelExport, PdfExport, ColumnChooser, Page]} />
        </GridComponent>
      </div>
    </div>
  );
};

export default React.memo(SalesReportDataGrid);
